import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function CommandSearch({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data.results);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const go = (url) => {
    setOpen(false);
    navigate(url);
  };

  const grouped = results.reduce((acc, r) => {
    (acc[r.type] = acc[r.type] || []).push(r);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-xl">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">Search DBRF capabilities, projects and disciplines.</DialogDescription>
        <div className="flex items-center gap-2 border-b border-[var(--line)] px-4">
          <Search size={18} className="text-[var(--mineral)]" />
          <input
            data-testid="global-search-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search capabilities, projects, disciplines…"
            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-[var(--mineral)]"
          />
        </div>
        <div className="max-h-[340px] overflow-y-auto p-2">
          {!q.trim() && (
            <p className="px-3 py-6 text-sm text-[var(--mineral)]">
              Type to search the DBRF catalogue. Press <kbd className="font-mono">/</kbd> anywhere to open.
            </p>
          )}
          {q.trim() && !loading && results.length === 0 && (
            <p className="px-3 py-6 text-sm text-[var(--mineral)]" data-testid="search-empty">No results for “{q}”.</p>
          )}
          {Object.entries(grouped).map(([type, items]) => (
            <div key={type} className="mb-2">
              <p className="px-3 py-1.5 font-mono text-[0.66rem] uppercase tracking-wide text-[var(--mineral)]">{type}</p>
              {items.map((r) => (
                <button
                  key={r.url + r.title}
                  data-testid="search-result-item"
                  onClick={() => go(r.url)}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left hover:bg-[var(--ivory)]"
                >
                  <span>
                    <span className="font-medium text-navy">{r.title}</span>
                    <span className="ml-2 font-mono text-xs text-[var(--mineral)]">{r.meta}</span>
                  </span>
                  <ArrowRight size={14} className="text-[var(--mineral)]" />
                </button>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
